import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastUtils } from './components/ToastUtils';
import DeleteModal from './components/DeleteModal';
import Sidebar from './components/Sidebar';
import ICEditor from './components/ICEditor';
import ICDisplay from './components/ICDisplay';
import NetlistEditor from './components/NetlistEditor';
import LoadingSpinner from './components/LoadingSpinner';
import CollapsiblePanel from './components/CollapsiblePanel';
import translations from './utils/translations';
import { createLoadingToast, updateToastWithSuccess, updateToastWithError, setupCustomStyles } from './components/ToastUtils';

// Setup custom styles for the application
setupCustomStyles();

const App = forwardRef(function App({ onNew, user, setTriedToSave, current_locale = 'en', isRTL = false }, ref) {
  // Get translations based on locale
  const t = translations[current_locale] || translations['en'];
  // IC state
  const [icName, setIcName] = useState('NewIC');
  const [verticalBins, setVerticalBins] = useState(0);
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [icToDelete, setIcToDelete] = useState(null);
  const [horizontalBins, setHorizontalBins] = useState(0);
  const [pinLabels, setPinLabels] = useState({});
  const [netlistContent, setNetlistContent] = useState('');
  const [netlistFirstLine, setNetlistFirstLine] = useState('');
  const [description, setDescription] = useState('');
  
  // Reference to track if netlist first line should be auto-generated
  const shouldGenerateNetlistFirstLine = useRef(true);
  
  // CRUD state
  const [savedICs, setSavedICs] = useState([]);
  const [currentIC, setCurrentIC] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading state
  const [isSaving, setIsSaving] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Create a ref to track if component is mounted
  const isMounted = React.useRef(true);
  
  // Fetch saved ICs from the API when component mounts
  useEffect(() => {
    isMounted.current = true;
    
    // Fetch saved ICs and only create a new IC if none exist
    const initializeICs = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/api/ics');
        
        if (isMounted.current) {
          setSavedICs(response.data);
          
          // Only create a new IC if there are no saved ICs
          // Otherwise, load the first IC from the list
          if (response.data.length === 0) {
            createNewIC();
          } else {
            // Load the first IC from the list
            await loadIC(response.data[0]);
          }
          
          setIsLoading(false);
          setInitialLoadComplete(true);
        }
      } catch (error) {
  
        if (isMounted.current) {
          // If there's an error, create a new IC as fallback
          createNewIC();
          setIsLoading(false);
          setInitialLoadComplete(true);
        }
      }
    };
    
    initializeICs();
    
    // Cleanup function to set mounted flag to false when unmounting
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Function to fetch saved ICs from the API
  const fetchSavedICs = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/ics');
      const icsData = Array.isArray(response.data) ? response.data : [];
      
      if (isMounted.current) {
        setSavedICs(icsData);
        setIsLoading(false);
      }
      return icsData;
    } catch (error) {

      if (isMounted.current) {
        setSavedICs([]);
        setIsLoading(false);
      }
      return [];
    }
  };
  
  // Create a new IC
  const createNewIC = () => {
    // Reset to default values
    setIcName('NewIC');
    
    // Set default dimensions
    const defaultVertical = 0;
    const defaultHorizontal = 0;
    setVerticalBins(defaultVertical);
    setHorizontalBins(defaultHorizontal);
    
    // Enable auto-generation for netlist first line
    shouldGenerateNetlistFirstLine.current = true;
    
    // Clear the netlist content and first line to ensure they're regenerated
    setNetlistContent('');
    setNetlistFirstLine('');
    
    // Initialize pin labels with default alphabets
    const newPinLabels = {};
    // Top pins
    for (let i = 0; i < defaultHorizontal; i++) {
      newPinLabels[`top-${i}`] = { label: String.fromCharCode(65 + i), checked: false }; // A, B, C, D...
    }
    // Right pins
    for (let i = 0; i < defaultVertical; i++) {
      newPinLabels[`right-${i}`] = { label: String.fromCharCode(75 + i), checked: false }; // K, L, M, N, O...
    }
    // Bottom pins
    for (let i = 0; i < defaultHorizontal; i++) {
      newPinLabels[`bottom-${i}`] = { label: String.fromCharCode(69 + i), checked: false }; // E, F, G, H...
    }
    // Left pins
    for (let i = 0; i < defaultVertical; i++) {
      newPinLabels[`left-${i}`] = { label: String.fromCharCode(80 + i), checked: false }; // P, Q, R, S, T...
    }
    setPinLabels(normalizePinLabels(newPinLabels));
    
    setNetlistContent('');
    setNetlistFirstLine('');
    setDescription('');
    setCurrentIC(null);
    
    // After setting pin labels, update the netlist first line
    setTimeout(() => {
      // Enable auto-generation before updating
      shouldGenerateNetlistFirstLine.current = true;
      updateNetlistFirstLine(newPinLabels);
    }, 10); // Slightly longer timeout to ensure all state is updated
  };
  
  // Save current IC
  const saveIC = async () => {
    // Validate IC name - no spaces allowed
    if (icName.includes(' ')) {
      const errorToastId = createLoadingToast('Validating IC name...');
      updateToastWithError(errorToastId, 'IC name cannot contain spaces');
      return;
    }
    
    // === CUSTOM: If user is null, call IcCreatorWrapper and pass 1 ===
    if (!user) {
      // You cannot "call" a React component, but you can trigger a parent action or show a message
      // For now, log and show a toast as a placeholder, and block the save
      console.warn('User is null, IcCreatorWrapper would be called with 1');
      // toast.info('User is null: IcCreatorWrapper(1) would be triggered here.');
      setTriedToSave(1);
      return;
    }
    try {
      // Create a loading toast notification
      const savingToastId = createLoadingToast(`Saving IC '${icName}'...`);
      
      // Set internal saving state (for UI elements that might need it)
      setIsSaving(true);
      
      // Track save event in Google Analytics
      if (typeof gtag === "function") {
        gtag("event", "save_ic", { 
          "event_category": "IC Creator", 
          "event_label": currentIC ? "Update IC" : "Create IC",
          "ic_name": icName
        });
      }
      
      // Combine the first line and the rest of the netlist for saving
      const combinedNetlist = netlistFirstLine ? 
        (netlistFirstLine + (netlistContent ? '\n' + netlistContent : '')) : 
        netlistContent;
      

      
      // Ensure pinLabels are always in the { label, checked } format before saving
// Backend must persist both label and checked for each pin, e.g. { "top-0": { label: "A", checked: true } }
// Prepare legacy pin_labels as plain strings and include initial_checked as a key inside
const legacyPinLabels = {};
Object.entries(pinLabels).forEach(([key, val]) => {
  legacyPinLabels[key] = typeof val === 'object' && val !== null ? val.label : val;
});
// Prepare initial_checked as an array of checked pin keys
const initialChecked = Object.entries(pinLabels)
  .filter(([key, val]) => val && typeof val === 'object' && !!val.checked)
  .map(([key]) => key);
legacyPinLabels.initial_checked = initialChecked;
const icData = {
  ic: {
    name: icName,
    vertical_pins: verticalBins,
    horizontal_pins: horizontalBins,
    pin_labels: legacyPinLabels,
    netlist: combinedNetlist,
    description: description,
    public: true
  }
};

      let response;
      
      if (currentIC) {
        // Update existing IC
        response = await axios.put(`/api/ics/${currentIC.id}`, icData);
      } else {
        // Create new IC
        response = await axios.post('/api/ics', icData);
      }

      // Refresh the list of saved ICs
      if (isMounted.current) {
        await fetchSavedICs();
        
        // Set current IC to the saved one
        setCurrentIC(response.data);
        
        // Turn off the internal saving state
        setIsSaving(false);
        
        // Enable auto-generation for netlist first line
        shouldGenerateNetlistFirstLine.current = true;
        
        // Update the toast notification with success message
        updateToastWithSuccess(savingToastId, `IC '${icName}' has been ${currentIC ? 'updated' : 'created'}!`);
        
        // === CUSTOM: If user is null, call IcCreatorWrapper and pass 1 ===
        if (!user) {
          // You cannot "call" a React component, but you can trigger a parent action or show a message
          // For now, log and show a toast as a placeholder
          // console.warn('User is null, IcCreatorWrapper would be called with 1');
          // toast.info('User is null: IcCreatorWrapper(1) would be triggered here.');
          //setTriedToSave(1);
          // If you want to trigger a navigation or rerender, pass a callback from parent and call it here
          // Example: if (onUserNull) onUserNull(1);
        }
      }
    } catch (error) {

      if (isMounted.current) {
        // Turn off the internal saving state
        setIsSaving(false);
        
        // Update the toast notification with error message
        updateToastWithError(savingToastId, `Error saving IC: ${error.message}`);
      }
    }
  };
  
  // Load an IC
  const loadIC = async (ic) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/ics/${ic.id}`);
      const loadedIC = response.data;
      
      if (isMounted.current) {
        setIcName(loadedIC.name);
        setVerticalBins(loadedIC.vertical_pins);
        setHorizontalBins(loadedIC.horizontal_pins);
        let pinLabelsRaw = loadedIC.pin_labels || {};
let initialChecked = [];
if (Array.isArray(pinLabelsRaw.initial_checked)) {
  initialChecked = pinLabelsRaw.initial_checked;
  // Remove initial_checked from pinLabels before normalization
  delete pinLabelsRaw.initial_checked;
}
let normalized = normalizePinLabels(pinLabelsRaw);
if (initialChecked.length > 0) {
  initialChecked.forEach(key => {
    if (normalized[key]) normalized[key].checked = true;
  });
}
setPinLabels(normalized);
        

        
        // Split the netlist into first line and the rest
        const netlistLines = (loadedIC.netlist || '').split('\n');

        
        // Handle the netlist content
        if (netlistLines.length > 0 && netlistLines[0]) {
          // Use the first line from the loaded netlist
          setNetlistFirstLine(netlistLines[0]);
          
          // Set the rest of the lines as the netlist content
          if (netlistLines.length > 1) {
            const bodyContent = netlistLines.slice(1).join('\n');

            setNetlistContent(bodyContent);
          } else {

            setNetlistContent('');
          }
        } else {
          // If there's no first line in the loaded netlist, generate one
          if (Object.keys(loadedIC.pin_labels || {}).length > 0) {
            const generatedFirstLine = generateNetlistFirstLine(loadedIC.pin_labels || {}, loadedIC.name);

            setNetlistFirstLine(generatedFirstLine);
          } else {
            setNetlistFirstLine('');
          }
          setNetlistContent('');
        }
        
        // Force a refresh of the first line after a short delay to ensure it reflects the correct pin labels
        setTimeout(() => {

          
          // Make sure the bins are set correctly before generating the first line
          setVerticalBins(loadedIC.vertical_pins);
          setHorizontalBins(loadedIC.horizontal_pins);
          
          // Temporarily enable auto-generation to allow the update
          const previousAutoGenState = shouldGenerateNetlistFirstLine.current;
          shouldGenerateNetlistFirstLine.current = true;
          
          // Wait a bit more to ensure the bins are updated
          setTimeout(() => {
            // Update the first line with the loaded pin labels
            const firstLine = generateNetlistFirstLine(loadedIC.pin_labels || {}, loadedIC.name);

            setNetlistFirstLine(firstLine);
            
            // Restore the previous auto-generation state
            shouldGenerateNetlistFirstLine.current = previousAutoGenState;
          }, 50);
        }, 100);
        
        setDescription(loadedIC.description || '');
        
        // For existing ICs, we'll initially disable auto-generation to preserve user data
        // It will be enabled when the user edits the IC name
        shouldGenerateNetlistFirstLine.current = false;
        
        // If auto-generation is enabled and we have pin labels, generate the netlist
        if (shouldGenerateNetlistFirstLine.current && Object.keys(loadedIC.pin_labels || {}).length > 0) {
          setTimeout(() => {
            updateNetlistFirstLine(loadedIC.pin_labels);
          }, 10);
        }
        setCurrentIC(loadedIC);
        setIsLoading(false);
      }
    } catch (error) {

      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };
  
  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    setIcToDelete(id);
    setDeleteModalOpen(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setIcToDelete(null);
  };

  // Delete an IC
  const deleteIC = async (id) => {
    openDeleteModal(id);
  };

  // Confirm and execute IC deletion
  const confirmDeleteIC = async () => {
    if (!icToDelete) return;
    
    // Close the modal immediately
    closeDeleteModal();
    
    // Create a loading toast notification
    const deletingToastId = createLoadingToast('Deleting IC...');
    
    try {
      setIsLoading(true);
      await axios.delete(`/api/ics/${icToDelete}`);
      
      if (isMounted.current) {
        // Refresh the list after deletion
        const updatedICs = await fetchSavedICs();
        
        // If we deleted the current IC or if there are no more ICs left
        if (currentIC && (currentIC.id === icToDelete || currentIC.id === parseInt(icToDelete)) || updatedICs.length === 0) {
          createNewIC(); // Reset form after deleting current IC
        }
        setIsLoading(false);
        
        // Update the toast notification with success message
        updateToastWithSuccess(deletingToastId, 'IC successfully deleted');
      }
    } catch (error) {

      if (isMounted.current) {
        // Ensure we have a valid state even after error
        setSavedICs(prevICs => Array.isArray(prevICs) ? prevICs : []);
        setIsLoading(false);
        
        // Update the toast notification with error message
        updateToastWithError(deletingToastId, 'Failed to delete IC');
      }
    }
  };
  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    createNewIC
  }));

  // Utility to normalize pinLabels to { label, checked } format
  function normalizePinLabels(pinLabels) {
    const normalized = {};
    Object.entries(pinLabels || {}).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null && 'label' in value) {
        normalized[key] = { label: value.label, checked: !!value.checked };
      } else {
        normalized[key] = { label: value || '', checked: false };
      }
    });
    return normalized;
  }

  // Initialize pin labels when dimensions change
  React.useEffect(() => {
    setPinLabels(prevPinLabels => {
      const newPinLabels = {};
      // Top pins
      for (let i = 0; i < horizontalBins; i++) {
        if (prevPinLabels[`top-${i}`]) {
          newPinLabels[`top-${i}`] = prevPinLabels[`top-${i}`];
        } else {
          newPinLabels[`top-${i}`] = { label: String.fromCharCode(65 + i), checked: false };
        }
      }
      // Right pins
      for (let i = 0; i < verticalBins; i++) {
        if (prevPinLabels[`right-${i}`]) {
          newPinLabels[`right-${i}`] = prevPinLabels[`right-${i}`];
        } else {
          newPinLabels[`right-${i}`] = { label: String.fromCharCode(74 + i), checked: false };
        }
      }
      // Bottom pins
      for (let i = 0; i < horizontalBins; i++) {
        if (prevPinLabels[`bottom-${i}`]) {
          newPinLabels[`bottom-${i}`] = prevPinLabels[`bottom-${i}`];
        } else {
          newPinLabels[`bottom-${i}`] = { label: String.fromCharCode(69 + i), checked: false };
        }
      }
      // Left pins
      for (let i = 0; i < verticalBins; i++) {
        if (prevPinLabels[`left-${i}`]) {
          newPinLabels[`left-${i}`] = prevPinLabels[`left-${i}`];
        } else {
          newPinLabels[`left-${i}`] = { label: String.fromCharCode(80 + i), checked: false };
        }
      }
      shouldGenerateNetlistFirstLine.current = true;
      setTimeout(() => updateNetlistFirstLine(newPinLabels), 10);
      return normalizePinLabels(newPinLabels);
    });
  }, [verticalBins, horizontalBins]);

  // Function to generate the first line of the netlist
  const generateNetlistFirstLine = (pinLabelsObj, customIcName = null) => {
    // Always use the label string for each pin (not the object)
    function getLabel(val) {
      if (typeof val === 'object' && val !== null && 'label' in val) {
        return val.label;
      }
      return val || '';
    }
    // Start with X followed by the IC name
    // Use customIcName if provided (for immediate updates), otherwise use state
    // Spaces are already removed during input, but we'll keep this as a safety measure
    let firstLine = `.subckt ${(customIcName || icName).replace(/\s+/g, '')}`; // Remove spaces from IC name
    // Add all pin labels in order: left, bottom, right, top
    const leftPins = [];
    const bottomPins = [];
    const rightPins = [];
    const topPins = [];
    
    // Use the actual pin labels object to determine how many pins to include
    // This ensures we include all pins that have labels, regardless of the current bin count
    const pinPositions = Object.keys(pinLabelsObj);
    
    // Extract pin counts from the pin labels
    let maxLeftIndex = -1;
    let maxBottomIndex = -1;
    let maxRightIndex = -1;
    let maxTopIndex = -1;
    
    pinPositions.forEach(pos => {
      const [side, indexStr] = pos.split('-');
      const index = parseInt(indexStr);
      
      if (side === 'left' && index > maxLeftIndex) maxLeftIndex = index;
      if (side === 'bottom' && index > maxBottomIndex) maxBottomIndex = index;
      if (side === 'right' && index > maxRightIndex) maxRightIndex = index;
      if (side === 'top' && index > maxTopIndex) maxTopIndex = index;
    });
    

    
    // Left pins (use max index from pin labels)
    const effectiveVerticalBins = Math.max(verticalBins, maxLeftIndex + 1, maxRightIndex + 1);
    for (let i = 0; i < effectiveVerticalBins; i++) {
      const label = getLabel(pinLabelsObj[`left-${i}`]);
      if (label) leftPins[effectiveVerticalBins - i - 1] = label;
    }
    
    // Bottom pins (use max index from pin labels)
    const effectiveHorizontalBins = Math.max(horizontalBins, maxBottomIndex + 1, maxTopIndex + 1);
    for (let i = 0; i < effectiveHorizontalBins; i++) {
      const label = getLabel(pinLabelsObj[`bottom-${i}`]);
      if (label) bottomPins[i] = label;
    }
    
    // Right pins (use max index from pin labels)
    for (let i = 0; i < effectiveVerticalBins; i++) {
      const label = getLabel(pinLabelsObj[`right-${i}`]);
      if (label) rightPins[effectiveVerticalBins - i - 1] = label;
    }
    
    // Top pins (use max index from pin labels)
    for (let i = 0; i < effectiveHorizontalBins; i++) {
      const label = getLabel(pinLabelsObj[`top-${i}`]);
      if (label) topPins[i] = label;
    }
    
    // Add pins in order: left, bottom, right, top
    // Filter out any undefined values and join with spaces
    var allPins = [];

    if (leftPins.length > 0 && bottomPins.length > 0){
      allPins = [...leftPins, ...bottomPins.reverse(), ...rightPins, ...topPins.reverse()]
    }

    if (leftPins.length > 0 && bottomPins.length === 0){
      allPins = [...leftPins, ...rightPins]
    }

    if (leftPins.length === 0 && bottomPins.length > 0){
      allPins = [...topPins, ...bottomPins]
    }
    

    // Filter out undefined/empty pins and join with spaces
    const filteredPins = allPins.filter(pin => pin !== undefined && pin !== '');
    const pinsString = filteredPins.join(' ');
    

    
    return `${firstLine} ${pinsString}`;
  };
  
  // Function to update the first line of the netlist
  const updateNetlistFirstLine = (newPinLabels, customIcName = null) => {
    // Skip if there are no pin labels
    const labelsToUse = newPinLabels || pinLabels;
    if (!labelsToUse || Object.keys(labelsToUse).length === 0) return;
    
    // Always generate the first line
    const firstLine = generateNetlistFirstLine(newPinLabels || pinLabels, customIcName);
    
    // Always set the first line in its own state variable, regardless of shouldGenerateNetlistFirstLine flag
    // This ensures the first line is always populated
    setNetlistFirstLine(firstLine);
    
    // Only update the content if auto-generation is enabled
    if (shouldGenerateNetlistFirstLine.current) {
      // Split the current netlist content by lines
      const lines = netlistContent.split('\n');
      
      // If the netlist is empty, keep it empty
      // Otherwise, keep all lines except the first one
      if (lines.length === 0 || (lines.length === 1 && lines[0] === '')) {
        setNetlistContent('');
      } else if (lines.length === 1) {
        // If there's only one line (the first line), clear the content
        setNetlistContent('');
      } else {
        // Keep all lines except the first one
        setNetlistContent(lines.slice(1).join('\n'));
      }
    }
  };

  const handlePinLabelChange = (position, value) => {
    setPinLabels(prev => {
      const prevObj = typeof prev[position] === 'object' && prev[position] !== null
        ? prev[position]
        : { label: prev[position] || '', checked: false };
      const newPinLabels = {
        ...prev,
        [position]: { ...prevObj, label: value }
      };
      setTimeout(() => {
        const firstLine = generateNetlistFirstLine(newPinLabels);
        setNetlistFirstLine(firstLine);
      }, 0);
      return newPinLabels;
    });
  };

  // Checkbox handler for pins
  const handlePinCheckboxChange = (position, checked) => {
    setPinLabels(prev => {
      const prevObj = typeof prev[position] === 'object' && prev[position] !== null
        ? prev[position]
        : { label: prev[position] || '', checked: false };
      const newPinLabels = {
        ...prev,
        [position]: { ...prevObj, checked }
      };
      return newPinLabels;
    });
  };

  const handleSave = async () => {
    // Create a loading toast notification
    const savingToastId = createLoadingToast('Saving Netlist...');
    
    // Set internal saving state (for UI elements that might need it)
    setIsSaving(true);
    
    try {
      // Combine the first line and the rest of the netlist for saving
      const combinedNetlist = netlistFirstLine ? 
        (netlistFirstLine + (netlistContent ? '\n' + netlistContent : '')) : 
        netlistContent;
      

      
      const response = await fetch('/api/saveNetlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: combinedNetlist }),
      });
      if (!response.ok) {
        throw new Error('Failed to save Netlist');
      }
      
      if (isMounted.current) {
        // Turn off the internal saving state
        setIsSaving(false);
        // Update the toast notification with success message
        updateToastWithSuccess(savingToastId, 'Netlist saved successfully!');
      }
    } catch (error) {

      
      if (isMounted.current) {
        // Turn off the internal saving state
        setIsSaving(false);
        
        // Update the toast notification with error message
        updateToastWithError(savingToastId, 'Failed to save Netlist');
      }
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      height: '100%', 
      overflow: 'hidden', 
      position: 'relative',
      backgroundColor: '#fff',
      borderRadius: '8px',
      maxHeight: '100vh'
    }}>
      {/* Delete Confirmation Modal */}
      <DeleteModal 
        isOpen={deleteModalOpen} 
        onClose={closeDeleteModal} 
        onConfirm={confirmDeleteIC}
        current_locale={current_locale}
        isRTL={isRTL}
      />
      
      {/* Loading Spinner */}
      {(isLoading && !isSaving) && <LoadingSpinner />}
      {initialLoadComplete && (
        <>
          {/* Left/Right Panel based on RTL - Saved ICs */}
          <CollapsiblePanel 
            title={t.savedICs} 
            position={isRTL ? "right" : "left"} 
            width="250px"
            defaultExpanded={true}
            current_locale={current_locale}
            isRTL={isRTL}
          >
            <Sidebar
              savedICs={savedICs}
              currentIC={currentIC}
              isLoading={isLoading}
              isSaving={isSaving}
              onCreateNewIC={createNewIC}
              onLoadIC={loadIC}
              onDeleteIC={deleteIC}
              onSaveIC={saveIC}
              current_locale={current_locale}
              isRTL={isRTL}
            />
          </CollapsiblePanel>
      
          {/* Center Area - IC Editor and Display */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            height: '100%',
            position: 'relative',
            boxShadow: 'inset 0 0 10px rgba(0,0,0,0.02)'
          }}>
            {/* Static Controls Area */}
            <div style={{
              padding: '15px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#ffffff',
              zIndex: 5,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <ICEditor 
                icName={icName}
                setIcName={setIcName}
                verticalBins={verticalBins}
                setVerticalBins={setVerticalBins}
                horizontalBins={horizontalBins}
                setHorizontalBins={setHorizontalBins}
                description={description}
                setDescription={setDescription}
                pinLabels={pinLabels}
                updateNetlistFirstLine={updateNetlistFirstLine}
                generateNetlistFirstLine={generateNetlistFirstLine}
                shouldGenerateNetlistFirstLine={shouldGenerateNetlistFirstLine}
                currentIC={currentIC}
                setCurrentIC={setCurrentIC}
                setSavedICs={setSavedICs}
                current_locale={current_locale}
                isRTL={isRTL}
              />
            </div>
            
            {/* Scrollable IC Display Area */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '15px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100%',
                padding: '20px 40px', /* Added more horizontal padding */
                width: 'calc(100% + 80px)', /* Make it wider than the container */
                marginLeft: '-40px', /* Offset the extra width */
                boxSizing: 'border-box'
              }}>
                <ICDisplay
                  horizontalBins={horizontalBins}
                  verticalBins={verticalBins}
                  pinLabels={pinLabels}
                  icName={icName}
                  handlePinLabelChange={handlePinLabelChange}
                  handlePinCheckboxChange={handlePinCheckboxChange}
                  current_locale={current_locale}
                  isRTL={isRTL}
                />
              </div>
            </div>
          </div>
      
          {/* Right/Left Panel based on RTL - Netlist */}
          <CollapsiblePanel 
            title={t.netlist} 
            position={isRTL ? "left" : "right"} 
            width="350px"
            backgroundColor="#f8f9fa"
            defaultExpanded={true}
            current_locale={current_locale}
            isRTL={isRTL}
          >
            <NetlistEditor
              netlistFirstLine={netlistFirstLine}
              netlistContent={netlistContent}
              setNetlistContent={setNetlistContent}
              handleSave={handleSave}
              current_locale={current_locale}
            />
          </CollapsiblePanel>
        </>
      )}
    </div>
  );
});

export default App;
